import axios from "axios";

import notify from "../assets/svg/utils/toast/Toast";
import { BASE_URL, ENDPOINT_URL } from "./urlConfig";
import { decryptResponse } from "../utils/encryption";

// --- Global Interceptors for all axios calls (including direct calls and instances) ---
// Note: We add these early so that any axios.create() calls might benefit if they share defaults,
// though technically instances created with axios.create have their own interceptor managers.
// For safety, we keep both global and instance-specific interceptors.

// Response interceptor to automatically decrypt any encrypted response
axios.interceptors.response.use(
  async (response) => {
    return await decryptResponse(response);
  },
  async (error) => {
    if (error.response) {
      try {
        error.response = await decryptResponse(error.response);
      } catch (decryptionError) {
        console.error("Error decrypting global error response:", decryptionError);
      }
    }
    return Promise.reject(error);
  },
);

// Global Request interceptor to add public key to ALL requests
axios.interceptors.request.use(
  (config) => {
    const RSA_PUBLIC_KEY =
      "-----BEGIN PUBLIC KEY-----\\nMIICIjANBgkqhkiG9w0BAQEFAAOCAg8AMIICCgKCAgEAqoCS48emTYtGkAGweSyw\\nE8swb7oRvP09cYB8t7bXTdxzO6uCOZvFbu++wx411roQ6hYN/YLMdfLnBv1u/z6K\\nnZ1/xnAgaJ3P1YL7+vXuo6zTXjmpO0xWC6DCU4ulUR7t9ezj7VdKOaDx4cydrZFB\\nBwH/5gOxajZKRkp4cmFctqeQaGalOBmD8sKVesVknbQgs1eodOdzk3g6Iob7S5hn\\niqLMP3cUyK+pvBBsdV3Ix5YQ7UnetHaBjzmvb4WCZxgnOo7BdMdOFIhWDnPALaJ3\\nws7sZU3oC9IIWMSWCMSwG4F7Wivn7Ww94HyK/Hlf4pVq0xEWltei43EqGq5aPXqP\\nXjPZq9UdY4drAvFZqbPf+mDCqTRIvyydyRpq+1i4zgX4lsaCXlVgj4PC/yoHO2jA\\nOspPSJA8GO58+wQLwOQZo/TOqJtlCpa/24cF8Br9rzgE9w7d+QdwGCwRe02s37g0\\nQWLyUgs9zmjh27pKHeuMx1ptnPRpusT8eDM91OE8zJC5YdHln2Z/F4S1VOP10a8A\\nJdRyiR37yZJI36TOY5rcHDfkCcq28+dvFVdmFT64YxEfRVOcEwNLQ41+E6JZgfY4\\ncxoLz7p2m2bw+byfooKofyO6XFI5OyHOIoZGqiLLxg7JWiVZjwiOMTYXUP7G3xOj\\nHXZs2j1GNwS1ZM3i1wI7mHkCAwEAAQ==\\n-----END PUBLIC KEY-----";
    config.headers["x-client-public-key"] = RSA_PUBLIC_KEY;
    return config;
  },
  (error) => Promise.reject(error),
);

const HEADERS = {
  "Api-Version": "v1",
  responseType: "application/json",
  "Content-Type": "application/json",
  Accept: "application/json",
};

const HEADERSMULTIPART = {
  "Api-Version": "v1",
  responseType: "application/json",
  "Content-Type": "multipart/form-data",
};

export const UnAuthApiService = axios.create({
  baseURL: BASE_URL,
  headers: HEADERS,
});

export const ApiService = axios.create({
  baseURL: BASE_URL,
  headers: HEADERS,
});

export const FileUploadService = axios.create({
  baseURL: BASE_URL,
  headers: HEADERSMULTIPART,
});

ApiService.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("access_token");

    if (token != null || token != undefined) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add RSA public key to headers (single line, newlines encoded)
    const RSA_PUBLIC_KEY =
      "-----BEGIN PUBLIC KEY-----\\nMIICIjANBgkqhkiG9w0BAQEFAAOCAg8AMIICCgKCAgEAqoCS48emTYtGkAGweSyw\\nE8swb7oRvP09cYB8t7bXTdxzO6uCOZvFbu++wx411roQ6hYN/YLMdfLnBv1u/z6K\\nnZ1/xnAgaJ3P1YL7+vXuo6zTXjmpO0xWC6DCU4ulUR7t9ezj7VdKOaDx4cydrZFB\\nBwH/5gOxajZKRkp4cmFctqeQaGalOBmD8sKVesVknbQgs1eodOdzk3g6Iob7S5hn\\niqLMP3cUyK+pvBBsdV3Ix5YQ7UnetHaBjzmvb4WCZxgnOo7BdMdOFIhWDnPALaJ3\\nws7sZU3oC9IIWMSWCMSwG4F7Wivn7Ww94HyK/Hlf4pVq0xEWltei43EqGq5aPXqP\\nXjPZq9UdY4drAvFZqbPf+mDCqTRIvyydyRpq+1i4zgX4lsaCXlVgj4PC/yoHO2jA\\nOspPSJA8GO58+wQLwOQZo/TOqJtlCpa/24cF8Br9rzgE9w7d+QdwGCwRe02s37g0\\nQWLyUgs9zmjh27pKHeuMx1ptnPRpusT8eDM91OE8zJC5YdHln2Z/F4S1VOP10a8A\\nJdRyiR37yZJI36TOY5rcHDfkCcq28+dvFVdmFT64YxEfRVOcEwNLQ41+E6JZgfY4\\ncxoLz7p2m2bw+byfooKofyO6XFI5OyHOIoZGqiLLxg7JWiVZjwiOMTYXUP7G3xOj\\nHXZs2j1GNwS1ZM3i1wI7mHkCAwEAAQ==\\n-----END PUBLIC KEY-----";
    config.headers["x-client-public-key"] = RSA_PUBLIC_KEY;

    return config;
  },
  async (error) => {
    return Promise.reject(error);
  },
);

const admin = localStorage.getItem("user_type");

ApiService.interceptors.response.use(
  async (response) => {
    // Decrypt response if encrypted
    const decryptedResponse = await decryptResponse(response);
    return decryptedResponse;
  },
  async (error) => {
    // Handle network errors or errors without response
    if (!error.response) {
      console.error("Network error or no response:", error);
      notify("error", "Network error. Please check your connection.");
      return Promise.reject(error);
    }

    try {
      error.response = await decryptResponse(error.response);
    } catch (decryptionError) {
      console.error("Error decrypting ApiService error response:", decryptionError);
    }

    const requestUrl = error?.config?.url || "";
    const impactPath = ENDPOINT_URL?.impactAnalysis?.impact_analysis || "";

    // If the failed request is the Impact Analysis API, suppress error toasts
    if (impactPath && requestUrl.includes(impactPath)) {
      return error.response;
    }

    if (error.response.status === 401) {
      notify("error", error?.response?.data?.message);

      if (admin !== "Admin") {
        window.location.href = "/";
        localStorage.removeItem("access_token");
      }

      return error.response;
    } else if (error.response.status === 404) {
      // notify("error", error?.response?.data?.message);
      return error.response;
    } else {
      return error.response;
    }
  },
);

FileUploadService.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("access_token");

    if (token != null) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add RSA public key to headers (single line, newlines encoded)
    const RSA_PUBLIC_KEY =
      "-----BEGIN PUBLIC KEY-----\\nMIICIjANBgkqhkiG9w0BAQEFAAOCAg8AMIICCgKCAgEAqoCS48emTYtGkAGweSyw\\nE8swb7oRvP09cYB8t7bXTdxzO6uCOZvFbu++wx411roQ6hYN/YLMdfLnBv1u/z6K\\nnZ1/xnAgaJ3P1YL7+vXuo6zTXjmpO0xWC6DCU4ulUR7t9ezj7VdKOaDx4cydrZFB\\nBwH/5gOxajZKRkp4cmFctqeQaGalOBmD8sKVesVknbQgs1eodOdzk3g6Iob7S5hn\\niqLMP3cUyK+pvBBsdV3Ix5YQ7UnetHaBjzmvb4WCZxgnOo7BdMdOFIhWDnPALaJ3\\nws7sZU3oC9IIWMSWCMSwG4F7Wivn7Ww94HyK/Hlf4pVq0xEWltei43EqGq5aPXqP\\nXjPZq9UdY4drAvFZqbPf+mDCqTRIvyydyRpq+1i4zgX4lsaCXlVgj4PC/yoHO2jA\\nOspPSJA8GO58+wQLwOQZo/TOqJtlCpa/24cF8Br9rzgE9w7d+QdwGCwRe02s37g0\\nQWLyUgs9zmjh27pKHeuMx1ptnPRpusT8eDM91OE8zJC5YdHln2Z/F4S1VOP10a8A\\nJdRyiR37yZJI36TOY5rcHDfkCcq28+dvFVdmFT64YxEfRVOcEwNLQ41+E6JZgfY4\\ncxoLz7p2m2bw+byfooKofyO6XFI5OyHOIoZGqiLLxg7JWiVZjwiOMTYXUP7G3xOj\\nHXZs2j1GNwS1ZM3i1wI7mHkCAwEAAQ==\\n-----END PUBLIC KEY-----";
    config.headers["x-client-public-key"] = RSA_PUBLIC_KEY;

    return config;
  },
  async (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor for FileUploadService
FileUploadService.interceptors.response.use(
  async (response) => {
    // Decrypt response if encrypted
    const decryptedResponse = await decryptResponse(response);
    return decryptedResponse;
  },
  async (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor for UnAuthApiService
UnAuthApiService.interceptors.response.use(
  async (response) => {
    // Decrypt response if encrypted
    const decryptedResponse = await decryptResponse(response);
    return decryptedResponse;
  },
  async (error) => {
    if (error.response) {
      try {
        error.response = await decryptResponse(error.response);
      } catch (decryptionError) {
        console.error("Error decrypting UnAuthApiService error response:", decryptionError);
      }
    }
    return Promise.reject(error);
  },
);
